import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import CmsImage from '../../components/CmsImage';
import Lightbox from '../../components/Lightbox';
import { fetchCollection, fetchLocalizedCollection } from '../../lib/cms';

export default function HomePageEn() {
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [cmsClients, setCmsClients] = useState([]);
  const [cmsCategories, setCmsCategories] = useState([]);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('faq', 'en').then((data) => {
      if (data && data.length) {
        setFaqs(data.sort((a, b) => (a.sortering || 0) - (b.sortering || 0)));
      }
    });

    fetchCollection('referencer').then((items) => {
      if (items && items.length) {
        const sorted = items.sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
        setCmsClients(
          sorted.map((item) => ({
            name: item.navn,
            img: Array.isArray(item.logo) ? item.logo[0] : item.logo,
            url: item.hjemmeside,
          }))
        );
      }
    });

    fetchLocalizedCollection('ydelser_produkter', 'en').then((items) => {
      if (items && items.length) {
        const primarySlugs = [
          'presser',
          'centralsmoering',
          'laser',
          'service-montage',
          'reservedele-sliddele',
        ];
        const getSlugVal = (it) =>
          typeof it?.slug === 'object'
            ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
            : it?.slug;
        const matched = primarySlugs
          .map((s) => items.find((it) => getSlugVal(it) === s))
          .filter(Boolean);

        if (matched.length >= 3) {
          setCmsCategories(
            matched.map((cat) => {
              const catSlug = getSlugVal(cat);
              const defaultLink =
                catSlug === 'laser'
                  ? '/en/laser'
                  : catSlug === 'service-montage'
                  ? '/en/service'
                  : catSlug === 'reservedele-sliddele'
                  ? '/en/spare-parts'
                  : catSlug === 'centralsmoering'
                  ? '/en/central-lubrication'
                  : catSlug === 'presser'
                  ? '/en/presses'
                  : `/en/${catSlug}`;
              return {
                title: cat.titel === 'Laser' ? 'MVD Products' : cat.titel,
                desc: cat.kort_beskrivelse,
                img: Array.isArray(cat.billede) ? cat.billede[0] : cat.billede,
                link: cat.cta_link || defaultLink,
              };
            })
          );
        }
      }
    });

    fetchLocalizedCollection('sider', 'en').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = items.find((s) => getSlugVal(s) === 'forside' || getSlugVal(s) === 'home');
      if (match) setPageData(match);
    });
  }, []);

  const defaultFaqsEn = [
    {
      id: '1',
      question: 'What warranty does PMB provide on machines and central lubrication?',
      answer:
        '<p>When purchasing new machines, products, and spare parts, a 12-month warranty is provided under normal operation of max. 8 hours daily. On newly installed central lubrication systems, we provide a full two-year warranty. On refurbished used machines, a 12-month warranty is also granted, while other used machinery is delivered with a 3-month warranty.</p>',
    },
    {
      id: '2',
      question: 'Why choose centralized lubrication from SKF and Lincoln?',
      answer:
        '<p>Centralized lubrication ensures uniform and precise lubricant metering, minimizes wear on bearings and bushings, reduces costly downtime, and saves up to 40% lubricant. Furthermore, our dedicated service team provides rapid nationwide on-site response.</p>',
    },
    {
      id: '3',
      question: 'Can a refurbished press be customized to our production requirements?',
      answer:
        '<p>Yes, every machine is thoroughly inspected and tested according to strict engineering standards. We can upgrade the press with new equipment, modern PLC control systems, or specialized surface treatments to match your exact production needs, and deliver it fully installed and ready for operation.</p>',
    },
    {
      id: '4',
      question: 'How quickly can PMB provide technical support and service?',
      answer:
        '<p>PMB provides nationwide service and technical support across Denmark. Our mobile service technicians can often be on-site within 24 hours, and we maintain an extensive warehouse inventory of original spare parts, pumps, and lubrication components.</p>',
    },
  ];

  const referenceClients = [
    {
      name: 'Mølbro',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/moelbro-1.webp',
    },
    {
      name: 'Kongskilde',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/kongskilde-1.webp',
    },
    {
      name: 'Maneta',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/maneta-1.webp',
    },
    {
      name: 'Pressalit',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pressalit-1.webp',
    },
    {
      name: 'Stantræk',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/stantraek-1.webp',
    },
    {
      name: 'Triplan',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/triplan-1.webp',
    },
    {
      name: 'Velux',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/velux-1.webp',
    },
    {
      name: 'Zeppelin',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/zeppelin-1.webp',
    },
  ];

  const primaryCategories = [
    {
      title: 'Presses',
      desc: 'Hydraulic presses, eccentric presses, and high-speed presses from leading European manufacturers.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/presser.png',
      link: '/en/presses',
    },
    {
      title: 'Central Lubrication',
      desc: 'Authorized distributor of SKF and Lincoln Central Lubrication. Optimal uptime and prolonged machine lifespan.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/centralsmoering.png',
      link: '/en/central-lubrication',
    },
    {
      title: 'MVD Products',
      desc: 'High-precision fiber laser cutting systems, guillotine shears, and press brakes (servo-electric and hydraulic).',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/bearbejdning.png',
      link: '/en/laser',
    },
    {
      title: 'Service & Assembly',
      desc: 'Nationwide technical service, repairs, modernizations, and statutory safety inspections for presses.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/service.png',
      link: '/en/service',
    },
    {
      title: 'Spare & Wear Parts',
      desc: 'Comprehensive warehouse inventory of original spare parts for PMB presses, lubrication systems, and metal sheet working.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/reservedele.png',
      link: '/en/spare-parts',
    },
  ];

  const heroImage =
    pageData && pageData.billede
      ? Array.isArray(pageData.billede)
        ? pageData.billede[0]
        : pageData.billede
      : 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/fremforing-hero.webp';

  const displayedCategories = cmsCategories.length > 0 ? cmsCategories : primaryCategories;
  const displayedClients = cmsClients.length > 0 ? cmsClients : referenceClients;

  return (
    <>
      <Helmet>
        <title>{pageData?.seo_titel || 'POVL MØLLERS MASKINFABRIK A/S'}</title>
        <meta
          name="description"
          content={
            pageData?.meta_beskrivelse ||
            'POVL MØLLERS MASKINFABRIK A/S in Borup offers professional manufacturing and machine engineering with focus on quality and precision. Contact us for more information.'
          }
        />
        <meta property="og:title" content={pageData?.seo_titel || 'POVL MØLLERS MASKINFABRIK A/S'} />
        <meta
          property="og:description"
          content={
            pageData?.intro ||
            'Machinery. Lubrication. Service. – since 1921. Presses, central lubrication and industrial sheet metal processing.'
          }
        />
        <meta
          property="og:image"
          content={heroImage}
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-[#121619] text-white overflow-hidden">
        {/* Background Image with subtle gradient overlay */}
        <div className="absolute inset-0 z-0">
          <CmsImage
            src={heroImage}
            alt="POVL MØLLERS MASKINFABRIK production"
            className="w-full h-full object-cover object-center opacity-65 filter brightness-95"
            width={1920}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121619]/80 via-[#121619]/45 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl">
            {/* Main H1 Headline */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
              {(() => {
                const title = pageData?.titel || 'Machinery. Lubrication. Service. – since 1921';
                if (title.includes('–')) {
                  const [main, ...rest] = title.split('–');
                  return (
                    <>
                      {main} <br className="hidden sm:inline" />
                      <span className="text-[#0d8086] font-normal">–{rest.join('–')}</span>
                    </>
                  );
                }
                return title;
              })()}
            </h1>

            {/* Subtitle / intro */}
            <p className="mt-6 text-lg sm:text-xl text-stone-300 leading-relaxed max-w-2xl">
              {pageData?.intro ||
                'When a machine stops, production stops. That is why our work is about more than machines and lubrication. With us, you do not just get a machine or a service agreement – you get a partner who takes responsibility for keeping your production running smoothly.'}
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/en/contact"
                className="px-7 py-4 rounded bg-[#006368] hover:bg-[#004d51] text-white font-bold text-sm tracking-wide transition shadow-lg flex items-center gap-2.5"
              >
                <span>Contact our specialists</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
              <Link
                to="/en/presses"
                className="px-7 py-4 rounded bg-white/10 hover:bg-white/20 text-white font-semibold text-sm tracking-wide border border-white/20 transition flex items-center gap-2"
              >
                <span>Explore our machinery program</span>
              </Link>
            </div>

            {/* Trust metric block */}
            <div className="mt-14 pt-8 border-t border-white/15 flex items-center gap-6">
              <div>
                <span className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                  7000+
                </span>
                <p className="text-xs uppercase tracking-wider text-stone-400 mt-0.5">
                  Companies assisted
                </p>
              </div>
              <div className="h-10 w-px bg-white/20" />
              <div>
                <span className="text-3xl sm:text-4xl font-extrabold text-[#0d8086] tracking-tight">
                  100+ years
                </span>
                <p className="text-xs uppercase tracking-wider text-stone-400 mt-0.5">
                  Experience & competence
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro statement section */}
      <section className="py-20 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#006368]">
                Our foundation
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 tracking-tight leading-snug">
                <strong>You</strong> know our expertise… and <strong>we</strong> know you!
              </h2>
              <div className="text-stone-700 space-y-4 text-base sm:text-lg leading-relaxed">
                <p>
                  We have already assisted more than <strong>7,000 companies</strong> with ensuring high
                  uptime, precise sheet metal processing, and reliable machine lines.
                </p>
                <p>
                  Our goal is to be your trusted partner when power, precision, and operational reliability
                  translate into lasting success in your day-to-day manufacturing.
                </p>
              </div>
              <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-sm font-semibold text-stone-800">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Technical advisory</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Customized systems</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Rapid support</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white p-8 rounded-lg border border-stone-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-full bg-[#006368]" />
                <h3 className="text-lg font-bold text-stone-900">Ask us – we are right here</h3>
                <p className="mt-3 text-stone-600 text-sm leading-relaxed">
                  Do you need technical insight, sparring on your project, or quick advice?
                  Our specialists are just one phone call away – and we answer before you wait.
                </p>

                <div className="mt-6 pt-6 border-t border-stone-100 space-y-3">
                  <a
                    href="tel:57526262"
                    className="flex items-center gap-3 text-[#006368] font-bold text-base hover:text-[#004d51] transition"
                  >
                    <span className="w-10 h-10 rounded bg-[#006368]/10 flex items-center justify-center">
                      <i className="fa-solid fa-phone" />
                    </span>
                    <span className="whitespace-nowrap">Call +45 57 52 62 62</span>
                  </a>
                  <a
                    href="mailto:info@pmborup.dk"
                    className="flex items-center gap-3 text-stone-700 hover:text-stone-900 text-sm font-medium transition"
                  >
                    <span className="w-10 h-10 rounded bg-stone-100 flex items-center justify-center">
                      <i className="fa-solid fa-envelope" />
                    </span>
                    <span className="whitespace-nowrap">info@pmborup.dk</span>
                  </a>
                </div>

                <div className="mt-6">
                  <Link
                    to="/en/contact"
                    className="w-full text-center block px-5 py-3 rounded bg-[#006368] text-white text-sm font-semibold hover:bg-[#004d51] transition"
                  >
                    Contact us here
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Primary Products & Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#006368]">
                Our expertise
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 tracking-tight mt-2">
                Primary products & services
              </h2>
            </div>
            <p className="text-stone-600 max-w-md text-sm leading-relaxed">
              We provide turnkey solutions from standalone quality machinery to complete
              industrialized production lines and nationwide technical service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 text-center w-full">
            {displayedCategories.map((cat, i) => {
              const total = displayedCategories.length;
              const remLg = total % 3;
              const remMd = total % 2;

              let colSpan = 'col-span-1';
              if (remMd === 1 && i === total - 1) {
                colSpan += ' md:col-span-12';
              } else {
                colSpan += ' md:col-span-6';
              }

              if (remLg === 1 && i === total - 1) {
                colSpan += ' lg:col-span-12';
              } else if (remLg === 2 && i >= total - 2) {
                colSpan += ' lg:col-span-6';
              } else {
                colSpan += ' lg:col-span-4';
              }

              return (
                <Link
                  key={i}
                  to={cat.link}
                  className={`w-full ${colSpan} group flex flex-col bg-stone-50 rounded-lg border border-stone-200 overflow-hidden hover:border-[#006368] hover:shadow-md transition duration-200 text-center items-center`}
                >
                  <div className="relative h-60 w-full overflow-hidden bg-white p-6 flex items-center justify-center border-b border-stone-200">
                    <CmsImage
                      src={cat.img}
                      alt={cat.title}
                      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      width={500}
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-between items-center w-full text-center">
                    <div className="w-full flex flex-col items-center">
                      <h3 className="text-xl font-bold text-stone-900 group-hover:text-[#006368] transition-colors text-center">
                        {cat.title}
                      </h3>
                      <p className="mt-2.5 text-sm text-stone-600 leading-relaxed text-center">
                        {cat.desc}
                      </p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-stone-200/80 flex items-center justify-center gap-2 text-sm font-semibold text-[#006368] group-hover:text-[#004f53] w-full">
                      <span>Read more</span>
                      <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Production & Competences editorial banner */}
      <section className="py-24 bg-[#1a2024] text-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#0d8086]">
                Technical competence
              </span>
              <h2 className="text-2xl sm:text-4xl font-bold tracking-tight text-white">
                The future of production power with experience and precision
              </h2>
              <div className="text-stone-300 space-y-4 text-sm sm:text-base leading-relaxed">
                <p>
                  At PMB, we view machines as the foundation of efficient manufacturing – where power,
                  precision, and operational reliability go hand in hand.
                </p>
                <p>
                  With more than 60 years of expertise in central lubrication and presses, and a close partnership with
                  leading European brands such as <strong>SKF, Lincoln, Hidroliksan, MVD, Zani, and Dimeco</strong>,
                  we deliver solutions tailored to your company's exact requirements.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6 pt-4 border-t border-stone-800">
                <div>
                  <h4 className="font-bold text-white text-base">Own workshop & warehouse</h4>
                  <p className="text-xs text-stone-400 mt-1">Extensive stock of spare parts, pumps, and lubrication parts in Borup.</p>
                </div>
                <div>
                  <h4 className="font-bold text-white text-base">Rapid response</h4>
                  <p className="text-xs text-stone-400 mt-1">Our mobile service team can often be on-site within 24 hours.</p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/en/about"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d8086] hover:text-white transition"
                >
                  <span>Read the full history from 1921 to today</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative rounded-lg overflow-hidden shadow-2xl bg-transparent">
                <CmsImage
                  src="https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/presse-pmb-2008-oppefra.webp"
                  alt="PMB Press from above"
                  className="w-full h-auto object-cover max-h-[480px]"
                  width={800}
                />
                <div className="p-4 bg-stone-900/90 text-xs text-stone-300 flex items-center justify-between">
                  <span>PMB eccentric press in operation</span>
                  <span className="text-[#0d8086] font-semibold">Quality & durability</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* References section - Dark background, slider moving to left, big logos, no boxes/borders */}
      <section className="py-20 bg-[#121619] border-y border-stone-800 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-[#0d8086]">
              Documented trust
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mt-2">
              Some of those who have already benefited from our expertise:
            </h2>
          </div>
        </div>

        {/* Sliding logo track moving continuously to the left */}
        <div className="relative w-full overflow-hidden">
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-[#121619] to-transparent z-10" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-[#121619] to-transparent z-10" />

          <div className="flex w-max items-center gap-12 sm:gap-20 animate-marquee py-4">
            {[...displayedClients, ...displayedClients].map((client, idx) => (
              <div
                key={idx}
                className="shrink-0 flex items-center justify-center p-2"
              >
                <CmsImage
                  src={client.img}
                  alt={client.name}
                  className="h-[89px] sm:h-[105px] max-h-[105px] w-auto object-contain opacity-85 hover:opacity-100 transition-opacity duration-200"
                  width={500}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - Split layout with dark surface, flush left image and accordion based on master design */}
      <section className="py-24 lg:py-32 bg-[#121619] text-white border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
            {/* Left Column: single large, flush-edged image that fills its container */}
            <div className="lg:col-span-5 flex">
              <div className="w-full h-full min-h-[420px] lg:min-h-[580px] relative overflow-hidden">
                <CmsImage
                  src="https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/fremforing-hero.webp"
                  alt="PMB machines and production"
                  className="w-full h-full object-cover object-center filter brightness-95"
                  width={900}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121619]/60 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>

            {/* Right Column: Content + Accordion */}
            <div className="lg:col-span-7 flex flex-col justify-center">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#0d8086]">
                  Frequently asked questions
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mt-2.5">
                  Questions & answers about our services
                </h2>
                <p className="mt-4 text-stone-400 italic text-base sm:text-lg leading-relaxed">
                  Get answers to the most common questions regarding our machinery, central lubrication systems, technical service agreements, and warranties.
                </p>
              </div>

              {/* Stacked accordion with subtle horizontal border lines */}
              <div className="mt-8 sm:mt-10 border-t border-stone-800 divide-y divide-stone-800">
                {(faqs.length > 0
                  ? faqs.map((f) => ({
                      id: f.id,
                      spoergsmaal: f.spoergsmaal_en || f.question || f.spoergsmaal,
                      svar: f.svar_en || f.answer || f.svar,
                    }))
                  : defaultFaqsEn.map((f) => ({
                      id: f.id,
                      spoergsmaal: f.question,
                      svar: f.answer,
                    }))
                ).map((faq, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div key={faq.id || idx} className="py-1">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                        aria-expanded={isOpen}
                        className="w-full py-5 flex items-center justify-between gap-4 text-left group cursor-pointer"
                      >
                        <span className="text-base sm:text-lg font-bold text-white group-hover:text-[#0d8086] transition-colors">
                          {faq.spoergsmaal}
                        </span>
                        <i
                          className={`fa-solid fa-chevron-down text-sm text-[#0d8086] transition-transform duration-200 shrink-0 ${
                            isOpen ? 'rotate-180 text-white' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="pl-0 sm:pl-4 pb-5 pt-0 text-sm sm:text-base text-stone-300 leading-relaxed animate-in fade-in duration-200">
                          <div
                            dangerouslySetInnerHTML={{ __html: faq.svar }}
                            className="prose prose-invert prose-stone max-w-none text-stone-300 leading-relaxed [&_p]:my-2"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox component */}
      <Lightbox
        images={referenceClients.map((c) => ({ url: c.img, title: c.name }))}
        initialIndex={activeGalleryIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
